import React from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { testimonialsData } from '../data/content';
import { FallbackUI } from './FallbackUI';

export const TestimonialsSection: React.FC = () => {
  const { lang, t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();

  const items = testimonialsData[lang];

  if (!items || items.length === 0) {
    return <FallbackUI message="Testimonials loading..." />;
  }

  return (
    <section id="testimonials" className="relative py-24 md:py-32 bg-vilarias-black overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-vilarias-accent/30 to-transparent" />

      <div className="section-padding relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.testimonials.headline}
          </h2>
          <div className="h-px w-16 bg-vilarias-accent mx-auto" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {items.slice(0, 3).map((item, index) => (
            <motion.div
              key={item.name + index}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.12 }}
            >
              <div className="glass-card rounded-2xl p-8 h-full hover:border-vilarias-accent/30 transition-all duration-300 relative">
                <Quote size={32} className="text-vilarias-accent/20 absolute top-6 right-6" />

                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star key={i} size={14} className="text-vilarias-accent fill-vilarias-accent" />
                  ))}
                </div>

                <p className="text-vilarias-text-secondary leading-relaxed mb-6 text-sm">
                  "{item.text}"
                </p>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-vilarias-accent to-vilarias-glow flex items-center justify-center text-white text-sm font-medium">
                    {item.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{item.name}</p>
                    <p className="text-vilarias-text-secondary text-xs">{item.role}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Second row for remaining testimonials */}
        {items.length > 3 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-6">
            {items.slice(3).map((item, index) => (
              <motion.div
                key={item.name + (index + 3)}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: (index + 3) * 0.12 }}
              >
                <div className="glass-card rounded-2xl p-8 h-full hover:border-vilarias-accent/30 transition-all duration-300 relative">
                  <Quote size={32} className="text-vilarias-accent/20 absolute top-6 right-6" />

                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }, (_, i) => (
                      <Star key={i} size={14} className="text-vilarias-accent fill-vilarias-accent" />
                    ))}
                  </div>

                  <p className="text-vilarias-text-secondary leading-relaxed mb-6 text-sm">
                    "{item.text}"
                  </p>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-vilarias-accent to-vilarias-glow flex items-center justify-center text-white text-sm font-medium">
                      {item.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{item.name}</p>
                      <p className="text-vilarias-text-secondary text-xs">{item.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
